import { useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { message } from "antd";
import AgGridPrintLayout from "./ag-grid-print-layout";
import type { DesignScopeCatalogPrintViewState } from "./model";
import {
  listDesignScopeCatalogItems,
  type DesignScopeCatalogItem
} from "pages/subscription-settings/module-settings/bootstrap-design-scope/apis";
import {
  CATALOG_UNCLASSIFIED,
  catalogItemDataPath,
  placeCatalogItemsBeforeNestedGroups,
  type CatalogTreeSystem
} from "pages/subscription-settings/module-settings/bootstrap-design-scope/catalog-items-hierarchy";
import {
  buildCatalogItemsListPrintColumnDefs,
  buildCatalogItemsPrintAutoGroupColumnDef,
  buildCatalogItemsPrintColumnDefs,
  type CatalogPrintRow
} from "pages/subscription-settings/module-settings/bootstrap-design-scope/catalog-items-print-columns";
import { designScopeActionErrorMessage } from "pages/subscription-settings/module-settings/bootstrap-design-scope/design-scope-toast-messages";

function DesignScopeCatalogPrintView({
  state
}: {
  state: DesignScopeCatalogPrintViewState;
}) {
  const apolloClient = useApolloClient();
  const gridRef = useRef<AgGridReact<CatalogPrintRow>>(null);
  const [items, setItems] = useState<DesignScopeCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);

  const hierarchy = Boolean(state.hierarchy);
  const treeSystem: CatalogTreeSystem = state.treeSystem === "MF" ? "MF" : "UF";

  useEffect(() => {
    if (!state.pdfFileBaseName) return undefined;
    const previousTitle = document.title;
    document.title = state.pdfFileBaseName;
    return () => {
      document.title = previousTitle;
    };
  }, [state.pdfFileBaseName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!state.catalogId) {
        message.error("Unable to open print view. Catalog is not available.");
        setItems([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const response = await listDesignScopeCatalogItems(
        apolloClient,
        state.catalogId
      );
      if (cancelled) return;
      if (!response.success || !Array.isArray(response.data)) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        setItems([]);
        setLoading(false);
        return;
      }
      setItems(response.data as DesignScopeCatalogItem[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [apolloClient, state.catalogId]);

  const rows = useMemo<CatalogPrintRow[]>(() => {
    const withIds = items.map((item, index) => ({
      ...item,
      displayId: index + 1
    }));
    if (!hierarchy) return withIds;
    return [...withIds].sort((a, b) => {
      const last = (row: CatalogPrintRow) =>
        catalogItemDataPath(row, treeSystem)[0] === CATALOG_UNCLASSIFIED
          ? 1
          : 0;
      return last(a) - last(b);
    });
  }, [hierarchy, items, treeSystem]);

  const columnDefs = useMemo(
    () =>
      hierarchy
        ? buildCatalogItemsPrintColumnDefs(treeSystem)
        : buildCatalogItemsListPrintColumnDefs(),
    [hierarchy, treeSystem]
  );
  const autoGroupColumnDef = useMemo(
    () =>
      hierarchy
        ? buildCatalogItemsPrintAutoGroupColumnDef(treeSystem)
        : undefined,
    [hierarchy, treeSystem]
  );

  return (
    <div data-testid="design-scope-catalog-print-view">
      <AgGridPrintLayout
        gridRef={gridRef}
        state={state}
        rowData={rows}
        rowLoading={loading}
        columnDefEntity={columnDefs}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData={hierarchy}
        getDataPath={
          hierarchy
            ? (data: CatalogPrintRow) => catalogItemDataPath(data, treeSystem)
            : undefined
        }
        postSortRows={
          hierarchy ? placeCatalogItemsBeforeNestedGroups : undefined
        }
        getRowId={(params: { data: CatalogPrintRow }) => params.data.id}
        gqlClient={apolloClient}
      />
    </div>
  );
}

export default DesignScopeCatalogPrintView;
